import { zipSync } from 'fflate';

export const PACKET_SIZE = 1024;
const HEADER_SIZE = 8;
const DATA_SIZE = PACKET_SIZE - HEADER_SIZE;
export const MAGIC = 0x7c7c;

export const CMD_SET_BUTTONS = 0x0001;
export const CMD_PARTIAL_UPDATE = 0x000d;
export const CMD_SET_BRIGHTNESS = 0x000a;
export const CMD_IN_BUTTON = 0x0101;

const INVALID_CHUNK_BYTES = [0x00, 0x7c];
const MAX_PADDING_RETRIES = 20;

function buildPacket(
  command: number,
  totalLength: number,
  data: Uint8Array,
): Uint8Array {
  const packet = new Uint8Array(PACKET_SIZE);
  const view = new DataView(packet.buffer);

  view.setUint16(0, MAGIC, false);
  view.setUint16(2, command, false);
  view.setUint32(4, totalLength, true);

  packet.set(data.subarray(0, DATA_SIZE), HEADER_SIZE);
  return packet;
}

export function buildBrightnessPacket(brightness: number): Uint8Array {
  const encoded = new TextEncoder().encode(String(brightness));
  return buildPacket(CMD_SET_BRIGHTNESS, encoded.length, encoded);
}

export interface ButtonEntry {
  col: number;
  row: number;
  png: Uint8Array;
}

export function buildButtonZip(buttons: ButtonEntry[]): Uint8Array {
  const manifest: Record<string, { State: number; ViewParam: object[] }> = {};
  const files: Record<string, Uint8Array> = {};

  for (const btn of buttons) {
    const key = `${btn.col}_${btn.row}`;
    const iconName = `${key}.png`;

    manifest[key] = {
      State: 0,
      ViewParam: [{ Icon: `icons/${iconName}`, Text: '' }],
    };

    files[`page/icons/${iconName}`] = btn.png;
  }

  files['page/manifest.json'] = new TextEncoder().encode(
    JSON.stringify(manifest, null, 2),
  );

  for (let attempt = 0; attempt < MAX_PADDING_RETRIES; attempt++) {
    if (attempt > 0) {
      files[`page/__pad_${attempt}`] = new TextEncoder().encode(
        `${Date.now()}_${attempt}_${Math.random()}`,
      );
    }

    const zip = zipSync(files, { level: 1 });

    if (validateChunkBoundaries(zip)) {
      return zip;
    }
  }

  throw new Error('Failed to build valid ZIP after max retries');
}

function validateChunkBoundaries(zip: Uint8Array): boolean {
  for (let offset = DATA_SIZE; offset < zip.length; offset += PACKET_SIZE) {
    if (INVALID_CHUNK_BYTES.includes(zip[offset])) {
      return false;
    }
  }
  return true;
}

export function splitIntoChunks(
  zip: Uint8Array,
  command: number,
): Uint8Array[] {
  const chunks: Uint8Array[] = [];

  const firstData = zip.subarray(0, DATA_SIZE);
  chunks.push(buildPacket(command, zip.length, firstData));

  for (let i = DATA_SIZE; i < zip.length; i += PACKET_SIZE) {
    const chunk = new Uint8Array(PACKET_SIZE);
    const remaining = zip.subarray(i, i + PACKET_SIZE);
    chunk.set(remaining);
    chunks.push(chunk);
  }

  return chunks;
}

export function parseButtonEvent(data: DataView): {
  button: number;
  pressed: boolean;
} | null {
  if (data.byteLength < 4) return null;

  const index = data.getUint8(1);
  const pressed = data.getUint8(3) === 0x01;

  return { button: index, pressed };
}
